import React, { useEffect, useMemo, useState } from "react";
import { ClientApiProps, FilterPicker, queryFromFilters, queryToFilters } from "../../common";
import { Client, FirewallPayload, FirewallStatus, Hint, IFilter, IFirewallAlphaTag, IMNO, ISettings, getApiClient } from "../../api";
import { Button, DataGrid, DataGridColumn, Header, downloadBlob } from "acma-spa";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation, useNavigate } from "react-router-dom";
import { notEmpty } from "../../stores/ListCache";
import { ImplementationDetails } from "./ImplementationDetails";
import { ContentModal } from "../ContentModal";
import { DeclineModal } from "./DeclineModal";
import { FirewalledAlphaTagStore } from "../../stores/FirewalledAlphaTagStore";

export interface FirewallPageProps extends ClientApiProps {
  settings: ISettings[]
  mnos: IMNO[],
  implementers: IMNO[]
}

export const FirewallPage: React.FC<FirewallPageProps> = (props) => {
  var location = useLocation();

  const [completing, setCompleting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [expanded, setExpanded] = useState<(IFirewallAlphaTag|null)[]>([]);
  const [rows, setRows] = useState<IFirewallAlphaTag[] | null>(null);
  const [filters, setFilters] = useState<IFilter[]>(() => {
    return queryToFilters(location.search);
  });
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [sorting, setSorting] = useState({ columnIndex: 0, descending: false });
  const [selection, setSelection] = useState<IFirewallAlphaTag[]>([]);

  const pageSize = 50;

  var navigate = useNavigate();

  const load = async (client: Client) => {
    if (pageNumber == 1) {
      setRows(null);
      console.log("Loading...")
    }

    try {
      const sortColumnList = [Hint.Name, Hint.Company, Hint.Contact, Hint.MNO, Hint.Status, Hint.CreatedOn];
      var sortColumn = Hint.Name;
      var sortDesc = false;
      if (sorting.columnIndex >= 0 && sorting.columnIndex < sortColumnList.length) {
        sortColumn = sortColumnList[sorting.columnIndex];
        sortDesc = sorting.descending;
      }

      var store = new FirewalledAlphaTagStore(client);

      var newRows = await store.getFirewalledAlphaTags(
        filters.filter(r => r.hint == Hint.Abn).map(r => r.value).filter(notEmpty),
        filters.filter(r => r.hint == Hint.Name).map(r => r.value).filter(notEmpty),
        filters.filter(r => r.hint == Hint.Contact).map(r => r.value).filter(notEmpty),
        filters.filter(r => r.hint == Hint.MNO).map(r => r.value).filter(notEmpty),
        filters.filter(r => r.hint == Hint.Status).map(r => r.value as FirewallStatus).filter(notEmpty),
        filters.filter(r => !r.hint).map(r => r.value)[0],
        sortColumn,
        sortDesc,
        pageNumber,
        pageSize,
        props.mnos
      );

      if (newRows.length < pageSize)
        setHasMore(false);

      pageNumber == 1 ? setRows(newRows) : setRows(rows?.concat(newRows) ?? newRows);
    }
    catch (e) {
      if (!rows)
        setRows([]);
    }
  }

  const reload = async () => {
    setSelection([]);

    if (pageNumber == 1) {
      const [client, abort] = getApiClient(
        props.baseUrl,
        props.b2cDomainName,
        props.msalClient
      );
      await load(client);
    }
    else {
      setPageNumber(1);
    }
  }

  useEffect(() => {
    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    load(client);

    return function () {
      abort.abort();
    }

  }, [filters, pageNumber, sorting])

  const getActions = (r?: FirewallStatus): [boolean, boolean] => {
    switch (r) {
      case FirewallStatus.Pending:
        return [true, true];
      case FirewallStatus.CompletedActionedByAll:
        return [false, true];
      case FirewallStatus.CompletedAwaitingOthers:
        return [false, true];
      case FirewallStatus.Declined:
        return [true, false];
      default:
        return [false, false];
    }
  }

  const columns: DataGridColumn<IFirewallAlphaTag>[] = [
    {
      label: "Alpha tag",
      value: r => <div onClick={e => setSelection([r])}>{r.name}</div>
    },
    {
      label: "Registrant",
      value: r => <div onClick={e => setSelection([r])}>{r.clientName}</div>
    },
    {
      label: "Contact",
      value: r => <div onClick={e => setSelection([r])}><a href={ `mailto:${r.contactEmail}`}>{r.contactName}</a></div>
    },
    {
      label: "Originating Network",
      value: r => <div onClick={e => setSelection([r])}>{props.mnos.filter(m => m.value == r.mnoId)[0]?.label ?? " "}</div>
    },
    {
      label: "Status",
      value: r => <div onClick={e => setSelection([r])}>{r.status ? r.status.split(/(?=[A-Z])/).join(" ") : ""}</div>
    },
    {
      label: "Created on",
      value: r => <div onClick={e => setSelection([r])}>{r.createdOn?.toLocaleDateString()}</div>
    }
  ]

  const onSort = (columnIndex: number, descending: boolean) => {
    if (columnIndex < 0) {
      columnIndex = sorting.columnIndex;
    }
    setSorting({ columnIndex, descending });
    setPageNumber(1);
  }

  const onFilter = (v: IFilter[]) => {
    setFilters(v);
    setPageNumber(1);
    navigate("?" + queryFromFilters(v));
  }

  const getFilter = async (s: string) => {
    if (!s)
      return [];

    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    return await client.getFirewalledAlphaTagFilters(s);
  }

  const onPaginate = () => {
    if (rows && rows.length > 0) {
      setPageNumber(pageNumber + 1)
    }
  }

  const csvExport = () => {
    downloadBlob(
      `${props.baseUrl}/api/Firewall/export`,
      props.msalClient,
      [`https://${props.b2cDomainName}.onmicrosoft.com/AcmaApis/Call`],
      `export.csv`,
      'text/csv'
    );
  }

  const onComplete = async (comments: string, declined: boolean) => {
    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    var store = new FirewalledAlphaTagStore(client);

    var res = await store.completeFirewall(new FirewallPayload({
      alphaTagIds: selection.map(r => r.alphaTagId).filter(notEmpty),
      comments: comments,
      declined: declined
    }))

    await reload();
  }


  const canComplete = selection.filter(r => getActions(r.status)[0] == true).length > 0;
  const canDecline = selection.filter(r => getActions(r.status)[1] == true).length > 0;
  const defaultOptions = useMemo(
    () => {
      var res = Object.keys(FirewallStatus)
        .map((r) => ({ label: r.split(/(?=[A-Z])/).join(" "), value: r, hint: Hint.Status }));
      res.sort((a, b) => a.label > b.label ? 1 : (a.label < b.label ? -1 : 0));
      return res;
    }, []);

  return <>
    <Header label="Alpha tags subject to Firewall Rules" />
    <div className="row">
      <div className="col-lg-7 mb-2 mt-1">
        <Button id="export" label="Export" onClick={() => csvExport()} className="mr-2 mt-2" />
        {canComplete ? <Button id="complete" label="Complete" onClick={() => setCompleting(true)} className="mr-2 mt-2" mode="secondary" /> : <></>}
        {canDecline ? <Button id="decline" label="Decline" onClick={() => setDeclining(true)} className="mr-2 mt-2" mode="secondary" /> : <></>}
      </div>
      <div className="col-lg-5 mb-2 mt-1">
        <FilterPicker id="picker" value={filters} onChange={onFilter} getOptions={getFilter} defaultOptions={defaultOptions} />
      </div>
    </div>

    <InfiniteScroll
      dataLength={rows?.length ?? 0} //This is important field to render the next data
      next={onPaginate}
      hasMore={hasMore}
      loader={rows == null ? <></> : <h4>Loading...</h4>}>
      <DataGrid<IFirewallAlphaTag>
        columns={columns}
        rows={rows ?? []}
        noRowsMessage={rows == null ? "Loading..." : "No confirmations found"}
        sort={{
          columnIndex: sorting.columnIndex,
          descending: sorting.descending,
          onChange: onSort
        }}
        select={{
          rows: selection,
          onChange: setSelection
        }}
        expand={{
          rows: expanded,
          onChange: setExpanded,
          content: r => <ImplementationDetails implementations={r?.implementations ?? []} implementers={props.implementers} />
        }}
      />
    </InfiniteScroll>

    <ContentModal open={completing} onClose={() => setCompleting(false)} onConfirm={() => onComplete('', false)} title="Confirmation" spinnerText="Completing...">
      <p>Do you want to mark <b>{selection.map(r => r.name).join(", ")}</b> alpha tag(s) as completed?</p>
    </ContentModal>
    <DeclineModal open={declining} onClose={() => setDeclining(false)} onConfirm={comments => onComplete(comments, true)} selection={selection} />
  </>
}